import React from 'react';
import { Button as BSButton } from 'react-bootstrap';
import type { ButtonVariant, ButtonSize, BaseComponentProps } from '../../types/common';

export interface ButtonProps extends BaseComponentProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  disabled?: boolean;
  loading?: boolean;
  children: React.ReactNode;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  type?: 'button' | 'submit' | 'reset';
  block?: boolean;
  outline?: boolean;
}

/**
 * Base Button component built on React Bootstrap
 * Provides consistent styling and behavior across the application
 */
export const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  disabled = false,
  loading = false,
  children,
  onClick,
  type = 'button',
  className = '',
  id,
  'data-testid': testId,
  block = false,
  outline = false,
  ...props
}) => {
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (loading || disabled) {
      event.preventDefault();
      return;
    }
    onClick?.(event);
  };

  const buttonVariant = outline ? `outline-${variant}` : variant;
  const buttonSize = size === 'md' ? undefined : size; // React Bootstrap doesn't have 'md', it's the default

  return (
    <BSButton
      variant={buttonVariant}
      size={buttonSize}
      disabled={disabled || loading}
      onClick={handleClick}
      type={type}
      className={`${block ? 'd-block w-100' : ''} ${className}`.trim()}
      id={id}
      data-testid={testId}
      {...props}
    >
      {loading && (
        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
      )}
      {children}
    </BSButton>
  );
};

export default Button;
